/**
 * My Bib — the member's home screen.
 *
 * Bib, then dues, then the numbers they care about, then what's next.
 * The payment CTA only appears when money is actually owed.
 */

import { useNavigate } from 'react-router-dom';
import { eventsLedCount, medalsFor, today, totalKm, volunteerHours } from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { MyBib as BibCardLarge, PbGrid } from '../../components/Bib';
import { DuesBar } from '../../components/DuesBar';
import { EventCard, trendingEventId } from '../../components/EventCard';
import { SelfPaySheet } from '../../components/sheets/MoneySheets';
import { PasswordSheet } from '../../components/sheets/PasswordSheet';
import { Button, Empty, Eyebrow, Stat, Stats } from '../../components/ui';

export function MyBibPage() {
  const { db, me, meId } = useClubData();
  const { openSheet } = useUI();
  const navigate = useNavigate();

  if (!me) return <Empty title="No member">Sign in again to see your bib.</Empty>;

  const year = String(new Date().getFullYear());
  const hours = volunteerHours(db.volunteer, me.id, year);
  const races = db.results.filter((r) => r.memberId === me.id).length;
  const medals = medalsFor(db.results, db.volunteer, me.id).filter((m) => m.on).length;

  const upcoming = db.events
    // today(), not toISOString(): in IST the UTC date is still yesterday until
    // 05:30, which put a finished 5 AM run in "your next runs" — and disagreed
    // with /m/events, which has always used today().
    .filter((e) => e.date >= today())
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 3);
  const hot = trendingEventId(db.events, today());

  return (
    <>
      {/*
        First sign-in on a password the COMMITTEE generated. It was read out
        of a WhatsApp message — treat it as a handover secret and nag, front
        and centre on the home screen, until they choose their own. The flag
        clears itself the moment they do (POST /me/password).
      */}
      {me.mustChangePassword && (
        <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3 border border-gold-300 bg-gold-50 px-4 py-3.5">
          <div>
            <div className="font-market text-[16px] font-bold tracking-[-0.015em]">
              Set your own password
            </div>
            <div className="mt-1 font-market text-[10.5px] leading-relaxed text-copy-muted">
              You signed in with a password the committee generated. Replace it with one only
              you know.
            </div>
          </div>
          <Button tone="d" onClick={() => openSheet(<PasswordSheet member={me} />)}>
            Change password
          </Button>
        </div>
      )}

      <BibCardLarge member={me} results={db.results} />

      <DuesBar member={me} onPay={() => openSheet(<SelfPaySheet member={me} />)} />

      <div className="mt-3.5">
        <Stats>
          {/* Neutral edges: these are facts, not statuses. Colour on a stat now
              means something (green = ok, red = overdue), so it must be earned. */}
          <Stat
            value={
              <>
                {totalKm(db.results, me.id).toFixed(0)}
                <span className="text-[13px] text-copy-label"> km</span>
              </>
            }
            label="Raced"
          />
          <Stat value={races} label="Races" />
          <Stat tone="good" numberTone="good" value={hours} label="Volunteer hrs" />
          <Stat value={medals} label="Medals earned" />
          {/* Events this member ran for the club. Counts COMPLETED ones — a
              job finished, not a job assigned — so it ticks up at the same
              moment the event's tab leaves their menu. */}
          <Stat value={eventsLedCount(db.events, me.id)} label="Events led" />
        </Stats>
      </div>

      <Eyebrow>Your next runs &amp; meetings</Eyebrow>
      {upcoming.length ? (
        upcoming.map((e) => (
          <EventCard
            key={e.id}
            event={e}
            db={db}
            meId={meId}
            trending={e.id === hot}
            onOpen={(id) => navigate(`/m/events/${id}`)}
          />
        ))
      ) : (
        <Empty>Nothing scheduled yet.</Empty>
      )}

      <Eyebrow>Personal bests</Eyebrow>
      <PbGrid memberId={me.id} results={db.results} />
    </>
  );
}
